import { debugError, debugLog } from './debugLog';

/**
 * Cart Debugging Utility
 * 
 * Helper functions to debug cart state issues
 */

export const debugCartState = () => {
  debugLog('=== CART DEBUG STATE ===');
  
  // Check localStorage with correct keys
  const localCart = localStorage.getItem('eruption-radio-cart'); // Correct key from context
  const shopifyCartId = localStorage.getItem('shopify-cart-id');
  const checkoutUrl = localStorage.getItem('eruption-radio-checkout-url');
  
  debugLog('Local cart data (eruption-radio-cart):', localCart);
  debugLog('Shopify cart ID:', shopifyCartId);
  debugLog('Checkout URL:', checkoutUrl);
  
  if (localCart) {
    try {
      const parsed = JSON.parse(localCart);
      debugLog('Parsed local cart:', parsed);
      debugLog('Local cart items count:', parsed.length || 0);
      debugLog('Local cart items:', parsed);
    } catch (error) {
      debugError('Failed to parse local cart:', error);
    }
  }
  
  debugLog('=== END CART DEBUG ===');
};

export const enableDebugMode = () => {
  debugLog('🔍 DEBUG MODE ENABLED - Redirects will be prevented');
  localStorage.setItem('checkout-debug-mode', 'true');
};

export const disableDebugMode = () => {
  debugLog('✅ DEBUG MODE DISABLED - Normal checkout behavior restored');
  localStorage.removeItem('checkout-debug-mode');
};

export const isDebugModeEnabled = () => {
  // Only enable debug mode in development AND when explicitly set
  return process.env.NODE_ENV === 'development' && 
         localStorage.getItem('checkout-debug-mode') === 'true';
};

export const clearDebugMode = () => {
  localStorage.removeItem('checkout-debug-mode');
  debugLog('🧹 Debug mode cleared');
};

// Persistent logging that survives page reloads
export const logToPersistentStorage = (message: string, data?: any) => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    message,
    data: data ? JSON.stringify(data, null, 2) : undefined
  };
  
  // Get existing logs
  const existingLogs = localStorage.getItem('checkout-debug-logs');
  const logs = existingLogs ? JSON.parse(existingLogs) : [];
  
  // Add new log
  logs.push(logEntry);
  
  // Keep only last 50 logs to prevent storage overflow
  if (logs.length > 50) {
    logs.splice(0, logs.length - 50);
  }
  
  // Save back to storage
  localStorage.setItem('checkout-debug-logs', JSON.stringify(logs));
  
  // Also log to console
  debugLog(`[PERSISTENT] ${message}`, data);
};

export const viewPersistentLogs = () => {
  const logs = localStorage.getItem('checkout-debug-logs');
  if (!logs) {
    debugLog('No persistent logs found');
    return [];
  }
  
  const parsedLogs = JSON.parse(logs);
  debugLog('=== PERSISTENT DEBUG LOGS ===');
  parsedLogs.forEach((log: any, index: number) => {
    debugLog(`${index + 1}. [${log.timestamp}] ${log.message}`);
    if (log.data) {
      debugLog('   Data:', JSON.parse(log.data));
    }
  });
  debugLog('=== END PERSISTENT LOGS ===');
  
  return parsedLogs;
};

export const clearPersistentLogs = () => {
  localStorage.removeItem('checkout-debug-logs');
  debugLog('Persistent logs cleared');
};

export const debugCheckoutMethod = () => {
  logToPersistentStorage('=== CHECKOUT METHOD DEBUG START ===');
  
  // Check consolidated checkout service
  try {
    const { checkoutService } = require('../services/checkoutService');
    const isTestMode = checkoutService.isTestMode();
    
    logToPersistentStorage('Checkout service test mode', isTestMode);
    logToPersistentStorage('Checkout service available', 'Consolidated checkoutService');
    
    // Check if browser supports checkout functionality
    const canRedirect = typeof window !== 'undefined' && 
                       typeof window.location !== 'undefined';
    
    logToPersistentStorage('Can redirect to checkout', canRedirect);
    
    // Check current cart state
    const localCart = localStorage.getItem('eruption-radio-cart');
    let cartItemCount = 0;
    if (localCart) {
      const parsed = JSON.parse(localCart);
      cartItemCount = parsed.length;
      logToPersistentStorage('Cart items for checkout', cartItemCount);
    }
    
    logToPersistentStorage('Debug mode enabled', isDebugModeEnabled());
    
    logToPersistentStorage('=== CHECKOUT METHOD DEBUG END ===');
    
    return {
      checkoutServiceAvailable: true,
      testMode: isTestMode,
      canRedirect,
      cartItemCount,
      debugMode: isDebugModeEnabled()
    };
  } catch (error) {
    logToPersistentStorage('Error in debugCheckoutMethod', error);
    return { error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const clearAllCartData = () => {
  debugLog('Clearing all cart data...');
  localStorage.removeItem('eruption-radio-cart'); // Correct key from context
  localStorage.removeItem('shopify-cart-id');
  localStorage.removeItem('eruption-radio-checkout-url');
  debugLog('Cart data cleared');
};

declare global {
  interface Window {
    debugCartState?: typeof debugCartState;
    clearAllCartData?: typeof clearAllCartData;
    debugCheckoutMethod?: typeof debugCheckoutMethod;
    enableDebugMode?: typeof enableDebugMode;
    disableDebugMode?: typeof disableDebugMode;
    clearDebugMode?: typeof clearDebugMode;
    viewPersistentLogs?: typeof viewPersistentLogs;
    clearPersistentLogs?: typeof clearPersistentLogs;
    logToPersistentStorage?: typeof logToPersistentStorage;
  }
}

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  window.debugCartState = debugCartState;
  window.clearAllCartData = clearAllCartData;
  window.debugCheckoutMethod = debugCheckoutMethod;
  window.enableDebugMode = enableDebugMode;
  window.disableDebugMode = disableDebugMode;
  window.clearDebugMode = clearDebugMode;
  window.viewPersistentLogs = viewPersistentLogs;
  window.clearPersistentLogs = clearPersistentLogs;
  window.logToPersistentStorage = logToPersistentStorage;
}
